import { useEffect, useState } from "react";
import { Modal } from "../../../components/ui/modal";
import Form from "../../../components/form/Form";
import Label from "../../../components/form/Label";
import Input from "../../../components/form/input/InputField";
import Button from "../../../components/ui/button/Button";

const apiUrl = process.env.REACT_APP_API_URL;

type AddRemedialActionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  cst?: string | number | null;
  compInspectionId?: string | number | null;
  inspectionId?: string | number | null;
  inspectionItem?: {
    compinspectiontask_id?: string | number;
    compinspectionitem_id?: string | number;
    inspectiontask_id?: string | number;
    task_id?: string | number;
    inspection_id?: string | number;
    inspectionid?: string | number;
    task_name?: string;
    remarks?: string;
  };
  onSaved?: () => void;
};

export default function AddRemedialActionModal({
  isOpen,
  onClose,
  cst,
  compInspectionId,
  inspectionId,
  inspectionItem,
  onSaved,
}: AddRemedialActionModalProps) {
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    actionDate: "",
    actionTitle: "",
    remarks: "",
  });

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      actionDate: new Date().toISOString().split("T")[0],
      actionTitle: "",
      remarks: "",
    });
    setErrorMessage("");
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const resolvedCst = cst ?? null;
    const resolvedInspectionId =
      inspectionId ??
      inspectionItem?.inspection_id ??
      inspectionItem?.inspectionid ??
      null;
    const taskId =
      inspectionItem?.inspectiontask_id ??
      inspectionItem?.task_id ??
      inspectionItem?.compinspectiontask_id ??
      null;

    const missingFields: string[] = [];
    if (!resolvedCst) missingFields.push("cst");
    if (!compInspectionId) missingFields.push("compinspection_id");
    if (!resolvedInspectionId) missingFields.push("inspection_id");
    if (!taskId) missingFields.push("inspectiontask_id");
    if (!formData.actionDate) missingFields.push("actiondate");
    if (!formData.actionTitle) missingFields.push("name");

    if (missingFields.length > 0) {
      setErrorMessage(`Missing required fields: ${missingFields.join(", ")}`);
      return;
    }

    const token = localStorage.getItem("token");
    const payload = {
      cst: resolvedCst,
      compinspection_id: compInspectionId,
      inspection_id: resolvedInspectionId,
      inspectiontask_id: taskId,
      actiondate: formData.actionDate,
      name: formData.actionTitle,
      remarks: formData.remarks,
    };

    try {
      setIsSubmitting(true);
      setErrorMessage("");
      const response = await fetch(`${apiUrl}/remedial_actions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      onClose();
      onSaved?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save remedial action";
      setErrorMessage(message);
      console.error("Error adding remedial action:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl p-6 m-4">
      <h4 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        Add New Remedial Action
      </h4>
      <Form onSubmit={handleSubmit}>
        {errorMessage && (
          <div className="mb-4 rounded-lg border border-error-300 bg-error-50 px-3 py-2 text-sm text-error-700">
            {errorMessage}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <Label>Action Date</Label>
            <Input type="date" name="actionDate" value={formData.actionDate} onChange={handleChange} />
          </div>
          <div>
            <Label>Action Title</Label>
            <Input
              name="actionTitle"
              value={formData.actionTitle}
              onChange={handleChange}
              placeholder="Enter action title"
            />
          </div>
          <div>
            <Label>Remedial Action Remarks</Label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              className="w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none text-gray-600 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              rows={3}
            />
          </div>
          <div>
            <Label>Remedial Action Remarks History</Label>
            <textarea
              value={inspectionItem?.remarks || ""}
              readOnly
              className="w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none text-gray-600 border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              rows={5}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <Button type="button" size="sm" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
